/**
 * The arithmetic and the decisions behind a coach resolving a client's weekly check-up.
 *
 * A coached client fills in the check-up and submits it. Their coach then opens it and
 * answers one question: does the calorie target the app recommends stand? The coach has
 * three answers — accept the recommendation, type a different target, or decline the
 * change altogether. This module holds the parts of that which are pure calculation:
 * what the new daily target becomes, how the per-meal targets follow it, and how the
 * check-up is marked afterwards.
 *
 * It also holds the rule for whether a target the coach typed but has not applied is
 * worth asking about before the screen closes.
 *
 * Kept free of Angular so a unit test can drive it
 * (`weekly-checkup-coach-resolution.test.ts`), the same way `weekly-checkup-outcome.ts`
 * holds the Program Update rules and `weekly-checkup-steps.ts` holds the step rules.
 * The client unit tests run under mocha with no browser and no DOM, so anything that
 * touches Angular cannot be tested there at all. Until this module existed none of the
 * calculation below could be reached by a test: it sat inside the check-up page
 * component, and the only thing exercising it was an end-to-end run in a real browser.
 *
 * Split out of `weekly-checkup-page.ts` by
 * https://github.com/cybsol/mh-physique/issues/5274.
 */

import { deepClone } from '@cf-shared/deep-helpers';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { INFOODS, Nutrients } from '@cf-shared/data-model/food';
import { CoachResolvedAction, Log_WeeklyCheckup, WeeklyCheckupCompletionAction, readPositiveCalories } from '@cf-shared/data-model/log-weekly-checkup';

/**
 * A resolve failure the coach is meant to see, as opposed to one that means the app is
 * broken. The two are caught in the same place and handled differently: this one shows
 * the coach a toast and is logged quietly, because the ordinary cause is that the
 * check-up changed underneath them — another coach resolved it first, say — and there is
 * nothing here for a developer to fix.
 */
export class CoachReviewValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CoachReviewValidationError';
  }
}

/**
 * Scale one meal's calorie target by a factor. Does nothing when the targets object is
 * missing or carries no calorie entry — a plan need not define every meal type.
 */
function scaleMealCalories(targets: Nutrients | undefined, scale: number): void {
  if (!targets || targets[INFOODS.Calories] === undefined) return;
  targets[INFOODS.Calories] = Math.round(targets[INFOODS.Calories] * scale);
}

/**
 * Overwrite a pending plan's daily calorie total with a typed target, and move the per-meal
 * targets and the weekly calorie total by the same proportion. Used by the coach's Modify and,
 * since #5456, by a person without a coach setting their own target in the check-up.
 *
 * The weekly total follows because the solver stores it as the daily total times 7 and a later
 * solve can rebuild fixed meal targets from it; left alone it would describe the plan before
 * the typed target. Example: 14,483 a week at 2,069 a day becomes 14,840 at 2,120.
 *
 * The per-meal numbers are scaled here rather than by re-running the solver, and that is
 * deliberate: the solver reads the daily target as the client's CURRENT intake and works
 * out an adjustment from it, so feeding it the coach's override would make it adjust the
 * override instead of honouring it.
 *
 * Mutates the plan it is given, because the caller goes on to write that same object to
 * the database.
 */
export function applyCustomCaloriesToPendingPlan(pendingPlan: FitnessPlan, customCalories: number): void {
  const newTarget = Math.round(customCalories);
  const oldActual = pendingPlan.actualDailyAverageIntake;
  // a plan with no daily total to scale from leaves the meal targets alone rather than
  // dividing by zero
  const scale = (oldActual && oldActual > 0) ? (newTarget / oldActual) : 1;
  pendingPlan.actualDailyAverageIntake = newTarget;
  pendingPlan.nominalDailyAverageIntake = newTarget;
  scaleMealCalories(pendingPlan.cutMealTargets, scale);
  scaleMealCalories(pendingPlan.bulkMealTargets, scale);
  scaleMealCalories(pendingPlan.fixedMealTargets, scale);
  // if the plan carries a weekly calorie total, move it too. a plan with no daily total to scale
  // from (the scale above fell back to 1) gets the new daily target times 7 instead
  const weeklyTargets = pendingPlan.weeklyTargets;
  if (weeklyTargets && weeklyTargets[INFOODS.Calories] !== undefined) {
    const hadDailyTotal = !!oldActual && oldActual > 0;
    weeklyTargets[INFOODS.Calories] = hadDailyTotal
      ? Math.round(weeklyTargets[INFOODS.Calories] * scale)
      : newTarget * 7;
  }
}

/** what `markTargetChangedIfSolveDidNot` needs to know about the moment Apply was pressed */
export interface TargetChangeContext {
  /** the moment Apply was pressed, as an ISO time in UTC; never the check-up log's own date */
  nowUtc: string;
  /** the plan check's body-weight estimate, when it has one */
  estimatedBW_kg?: number;
}

/**
 * Keep the plan's "target last changed" record true after a person without a coach set their own
 * target in the check-up (#5456). Mutates `scaledPlan`, the plan about to be saved.
 *
 * Two things, and only when the live plan has such a record at all:
 *
 * - **The direction is always set to 0.** That is what the solver itself writes for a hand-typed
 *   target. Any other value arms the rule that drops an opposite-direction adjustment for 13
 *   days. Example: BORI is on track at 2,178 and sets 2,120. With a stored direction of -1, a
 *   recommendation to raise his target next Sunday would be silently dropped; with 0 it is offered.
 * - **The date and body weight move only when the solve did not move them** and the target really
 *   changed. A solve that decided "do nothing" leaves the old date, so without this next week's
 *   progress check would mix days on the old target with days on the new one.
 *
 * The coach's Modify does not call this: it writes what it wrote before #5456, apart from the
 * weekly total.
 */
export function markTargetChangedIfSolveDidNot(scaledPlan: FitnessPlan, livePlan: FitnessPlan, context: TargetChangeContext): void {
  const liveRecord = livePlan.nutritionAdjusted;
  // if the live plan has no record of when its target last changed, there is nothing to keep true
  if (!liveRecord || !scaledPlan.nutritionAdjusted) return;
  const solveMovedTheDate = scaledPlan.nutritionAdjusted.timestamp_utc !== liveRecord.timestamp_utc;
  const targetChanged = Math.round(scaledPlan.actualDailyAverageIntake) !== Math.round(livePlan.actualDailyAverageIntake);
  if (!solveMovedTheDate && targetChanged) {
    scaledPlan.nutritionAdjusted = {
      ...scaledPlan.nutritionAdjusted,
      timestamp_utc: context.nowUtc,
      BW_kg: context.estimatedBW_kg ?? liveRecord.BW_kg,
      BF_pc: liveRecord.BF_pc,
    };
  }
  scaledPlan.nutritionAdjusted.lastAdjustmentDirection = 0;
}

/**
 * The plan keys that training and the workout solver own: the program itself, its backup,
 * and the plateau and volume state that moves with it. The check-up's solve only reads
 * `workouts` and `subSlots` (to sum the weekly sets) and writes none of these.
 *
 * They are kept as one group because they are written together: a program upgrade at a
 * plateau sets a sub-slot's rebuild mark, `trainingLevels` and `needsReplan` in one go,
 * so keeping only some would leave a rebuild mark with no replan behind it.
 */
export const TRAINING_OWNED_PLAN_KEYS: readonly (keyof FitnessPlan)[] = [
  'workouts', 'subSlots', 'generatedPlanBackup', 'trainingLevels', 'needsReplan', 'trainingVolumeWeeklyAvg',
];

/**
 * Put the client's live training program onto the plan the coach is about to apply.
 *
 * The pending plan is the whole plan as it was when the client SUBMITTED the check-up,
 * and the coach may answer days later. Applying it as it is would roll back everything
 * training did in between: target weights from workouts the client did, set counts and
 * exercises changed in Edit Workouts (#6500). The check-up only means to change the
 * nutrition part, so the training-owned keys are taken from the live plan, and every
 * other key still comes from the pending plan.
 *
 * A key the live plan does not have is removed from the pending plan, so an old value
 * is not brought back. Mutates the pending plan, because the caller goes on to write
 * that same object to the database.
 */
export function keepLiveTrainingProgram(pendingPlan: FitnessPlan, livePlan: Readonly<FitnessPlan>): void {
  const pending = pendingPlan as unknown as Record<string, unknown>;
  const live = livePlan as unknown as Record<string, unknown>;
  for (const key of TRAINING_OWNED_PLAN_KEYS) {
    // if the live plan has the key, a copy of it replaces the submitted value. plain values
    // (needsReplan, trainingVolumeWeeklyAvg) are copied as they are: deepClone reports a
    // non-object as a programming mistake.
    const liveValue = live[key];
    if (liveValue !== undefined) {
      pending[key] = (typeof liveValue === 'object') ? deepClone(liveValue) : liveValue;
    } else {
      delete pending[key];
    }
  }
}

/** What one coach resolution does to the check-up: the new daily target, and how it is marked. */
export interface CoachResolutionOutcome {
  /** the daily calorie target to store, or undefined when the coach declined and nothing changes */
  newCalorieTarget: number | undefined;
  /** how the check-up itself is marked once the coach has answered */
  completionAction: WeeklyCheckupCompletionAction;
}

/**
 * Work out what a coach's answer means for the check-up.
 *
 * Accepting and modifying both end with the client on a new plan, so both mark the
 * check-up Accepted — what separates them is only the number, which is already in the
 * pending plan by the time this is called. Declining leaves the plan alone, so there is
 * no target and the check-up is marked Declined.
 */
export function getCoachResolutionOutcome(
  resolution: CoachResolvedAction,
  pendingPlan: FitnessPlan | undefined,
): CoachResolutionOutcome {
  return {
    newCalorieTarget: pendingPlan ? Math.round(pendingPlan.actualDailyAverageIntake) : undefined,
    completionAction: (resolution === CoachResolvedAction.Declined)
      ? WeeklyCheckupCompletionAction.Declined
      : WeeklyCheckupCompletionAction.Accepted,
  };
}

/**
 * Whether a calorie target the coach has typed is worth asking about before the screen
 * closes.
 *
 * The number lives only on screen until Apply writes it, so every way of leaving throws
 * it away. But asking in the wrong state puts a dialog in front of a coach who typed
 * nothing, so three states answer no:
 *
 * - the field is not open, so nothing was typed;
 * - the field still holds the recommendation it was seeded with, compared as whole
 *   calories because that is what the field shows and what Apply stores;
 * - the field is empty or holds a number Apply would refuse anyway.
 *
 * Cancel inside the field closes it, which is the first case — that is how the screen
 * satisfies "Cancel must never ask" (`design-docs/angular-components.md`) without a
 * separate leave-without-asking flag.
 */
export function hasUnsavedCoachCalorieTarget(
  isModifyFieldOpen: boolean,
  typedCalories: number | undefined,
  recommendedCalories: number,
): boolean {
  if (!isModifyFieldOpen) return false;
  if (!typedCalories || typedCalories <= 0) return false;
  return Math.round(typedCalories) !== recommendedCalories;
}

/**
 * Mark a check-up as resolved by its coach. Changes ONLY the fields a resolution owns and leaves
 * everything else on the document as it was, including what the client's submission stored
 * (the ticked days and the progress snapshot of #5456 among them).
 *
 * Mutates the check-up it is given, because the caller goes on to save that same object.
 *
 * @param resolution what the coach chose
 * @param outcome the answer of `getCoachResolutionOutcome` for that choice
 * @param resolvedAtUtc the moment of the resolution, as an ISO time in UTC
 *
 * When the coach typed their own target, the app's own recommendation is kept in
 * `appRecommendedAvgCalories` before it is replaced (#5456), so the completed check-up can say
 * what the app had recommended.
 */
export function applyCoachResolutionToCheckup(
  checkup: Log_WeeklyCheckup,
  resolution: CoachResolvedAction,
  outcome: CoachResolutionOutcome,
  resolvedAtUtc: string,
): void {
  // if the coach typed their own target, that is what the check-up now records as recommended
  if (resolution === CoachResolvedAction.Modified && outcome.newCalorieTarget !== undefined) {
    // keep what the app recommended, once: a retry after a failed save must not record the coach's number as the app's
    const appRecommended = readPositiveCalories(checkup.recommendedAvgCalories);
    if (checkup.appRecommendedAvgCalories === undefined && appRecommended !== undefined) {
      checkup.appRecommendedAvgCalories = Math.round(appRecommended);
    }
    checkup.recommendedAvgCalories = outcome.newCalorieTarget;
    checkup.coachModifiedCalories = outcome.newCalorieTarget;
  }
  checkup.completionAction = outcome.completionAction;
  checkup.coachResolvedAction = resolution;
  checkup.coachResolvedAtUtc = resolvedAtUtc;
  // the pending documents have been answered; a retry must not find them still attached
  checkup.pendingNewPlan = undefined;
  checkup.pendingNewFitnessState = undefined;
}
